<script setup lang="ts" generic="T extends { uuid: string; name: string }">
const props = withDefaults(
  defineProps<{
    items: T[]
    itemTo: (item: T) => string
    to?: string
    link?: boolean
    overflowTheme?: "light" | "dark"
  }>(),
  { link: true },
)

const { t } = useI18n()
const router = useRouter()
const sortedItems = computed(() => [...props.items].sort((a, b) => a.name.localeCompare(b.name)))
const firstItem = computed(() => sortedItems.value[0])

function goToItem(item: T) {
  router.push(props.itemTo(item))
}
</script>

<template>
  <div v-if="firstItem" class="inline-flex min-w-0 items-center gap-1.5">
    <ULink
      v-if="link"
      v-overflow="{ theme: overflowTheme }"
      :to="to ?? itemTo(firstItem)"
      class="min-w-0 truncate text-left"
      :exact="true"
      active-class="text-rnxblue-500"
    >
      <slot name="name" :item="firstItem">{{ firstItem.name }}</slot>
    </ULink>
    <span v-else v-overflow="{ theme: overflowTheme }" class="min-w-0 truncate">
      <slot name="name" :item="firstItem">{{ firstItem.name }}</slot>
    </span>
    <div v-if="sortedItems.length > 1" class="shrink-0" @click.stop>
      <USelectMenu
        :options="sortedItems"
        by="uuid"
        option-attribute="name"
        searchable
        clear-search-on-close
        :searchable-placeholder="t('search')"
        :popper="{ placement: 'bottom-start', strategy: 'fixed' }"
        :ui-menu="{ width: 'w-72 max-w-[calc(100vw-2rem)]' }"
        @change="goToItem"
      >
        <button type="button" class="inline-flex">
          <!-- inherits the text color so the counter matches the first item's name -->
          <Badge color="gray" class="whitespace-nowrap !text-inherit">{{
            t("more", { count: sortedItems.length - 1 })
          }}</Badge>
        </button>
        <template #option="{ option }">
          <span class="break-words" :title="option.name">{{ option.name }}</span>
        </template>
      </USelectMenu>
    </div>
  </div>
  <span v-else>—</span>
</template>

<i18n lang="yaml">
en:
  more: +{count} more
  search: Search…
fr-FR:
  more: +{count} autres
  search: Rechercher…
</i18n>
